import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
    TextField, Button, Box, Typography, Paper, Alert, 
    CircularProgress, Select, MenuItem, FormControl, InputLabel 
} from '@mui/material';
import stateDistricts from '../data/stateDistricts';
import CompletionMeter from '../components/common/CompletionMeter';
import { isValidIndianPhoneNumber, isValidEmail } from '../utils/validation';

function PostLostPage() {
    const { token } = useAuth(); // Use context instead of localStorage
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        itemName: '', 
        description: '', 
        dateLost: '', 
        locationLost: '', 
        extraPoints: '',
        contactPhone: '', 
        contactWhatsapp: '', 
        contactEmail: '',
        state: '', 
        district: '', 
        category: ''
    });
    const [image, setImage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [completion, setCompletion] = useState(0);
    const [categories, setCategories] = useState([]);
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const { data } = await axios.get('/api/categories');
                setCategories(data);
            } catch (error) {
                console.error("Could not fetch categories");
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        const fields = {
            itemName: { value: formData.itemName, weight: 20 },
            description: { value: formData.description, weight: 20 },
            image: { value: image, weight: 15 },
            dateLost: { value: formData.dateLost, weight: 10 },
            locationLost: { value: formData.locationLost, weight: 10 },
            state: { value: formData.state, weight: 5 },
            district: { value: formData.district, weight: 5 },
            category: { value: formData.category, weight: 5 },
            contactPhone: { value: formData.contactPhone, weight: 5 },
            contactEmail: { value: formData.contactEmail, weight: 5 },
        };
        let currentScore = 0;
        for (const key in fields) {
            if (fields[key].value) {
                currentScore += fields[key].weight;
            }
        }
        setCompletion(currentScore);
    }, [formData, image]);

    const validateForm = () => {
        const newErrors = {};
        const { contactPhone, contactWhatsapp, contactEmail } = formData;

        if (!contactPhone && !contactWhatsapp && !contactEmail) {
            newErrors.contact = 'Please provide at least one contact method.';
        }
        if (contactPhone && !isValidIndianPhoneNumber(contactPhone)) {
            newErrors.contactPhone = 'Please enter a valid 10-digit Indian mobile number.';
        }
        if (contactWhatsapp && !isValidIndianPhoneNumber(contactWhatsapp)) {
            newErrors.contactWhatsapp = 'Please enter a valid 10-digit Indian mobile number for WhatsApp.';
        }
        if (contactEmail && !isValidEmail(contactEmail)) {
            newErrors.contactEmail = 'Please enter a valid email address.';
        }

        setFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleImageChange = (e) => {
        setImage(e.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        const postData = new FormData();
        Object.keys(formData).forEach(key => {
             if (key === 'extraPoints') {
                postData.append(key, JSON.stringify(formData[key].split(',').map(s => s.trim()).filter(Boolean)));
            } else {
                postData.append(key, formData[key]);
            }
        });
        if (image) {
            postData.append('image', image);
        }
        try {
            const { data } = await axios.post('/api/items/lost', postData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                }
            });
            setSuccess(data.message || 'Lost item reported successfully!');
            setTimeout(() => navigate('/my-lost-items'), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to report item.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" gutterBottom>Report a Lost Item</Typography>
                <CompletionMeter percentage={completion} />
            </Box>
            
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            {formErrors.contact && <Alert severity="warning" sx={{ mb: 2 }}>{formErrors.contact}</Alert>}

            <form onSubmit={handleSubmit}>
                <TextField name="itemName" label="Item Name" fullWidth margin="normal" required onChange={handleChange} />
                <TextField name="description" label="Description" fullWidth margin="normal" multiline rows={4} required onChange={handleChange} />
                
                <Typography sx={{ mt: 2, color: 'text.secondary' }}>Contact Information (Provide at least one)</Typography>
                <TextField 
                    name="contactPhone" 
                    label="Contact Phone" 
                    fullWidth 
                    margin="normal" 
                    onChange={handleChange}
                    error={!!formErrors.contactPhone}
                    helperText={formErrors.contactPhone}
                />
                <TextField 
                    name="contactWhatsapp" 
                    label="Contact WhatsApp" 
                    fullWidth 
                    margin="normal" 
                    onChange={handleChange}
                    error={!!formErrors.contactWhatsapp}
                    helperText={formErrors.contactWhatsapp}
                />
                <TextField 
                    name="contactEmail" 
                    label="Contact Email" 
                    type="email" 
                    fullWidth 
                    margin="normal" 
                    onChange={handleChange}
                    error={!!formErrors.contactEmail}
                    helperText={formErrors.contactEmail}
                />

                <TextField name="dateLost" label="Date Lost" type="date" fullWidth margin="normal" InputLabelProps={{ shrink: true }} onChange={handleChange} />
                <TextField name="locationLost" label="Location Lost" fullWidth margin="normal" onChange={handleChange} />
                <TextField name="extraPoints" label="Identifying Marks (comma separated)" fullWidth margin="normal" onChange={handleChange} />
                
                <FormControl fullWidth margin="normal" required>
                    <InputLabel>Category</InputLabel>
                    <Select name="category" value={formData.category} label="Category" onChange={handleChange}>
                        {categories.map(cat => <MenuItem key={cat._id} value={cat.name}>{cat.name}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl fullWidth margin="normal">
                    <InputLabel>State</InputLabel>
                    <Select name="state" value={formData.state} label="State" onChange={handleChange}>
                        {Object.keys(stateDistricts).map(state => <MenuItem key={state} value={state}>{state}</MenuItem>)}
                    </Select>
                </FormControl>
                {formData.state && (
                    <FormControl fullWidth margin="normal">
                        <InputLabel>District</InputLabel>
                        <Select name="district" value={formData.district} label="District" onChange={handleChange}>
                            {stateDistricts[formData.state].map(district => <MenuItem key={district} value={district}>{district}</MenuItem>)}
                        </Select>
                    </FormControl>
                )}
                <Button variant="contained" component="label" sx={{ mt: 2 }}>
                    Upload Image
                    <input type="file" hidden onChange={handleImageChange} />
                </Button>
                {image && <Typography sx={{ display: 'inline', ml: 2 }}>{image.name}</Typography>}
                <br/>
                <Button type="submit" variant="contained" disabled={loading} sx={{ mt: 2 }}>
                    {loading ? <CircularProgress size={24} /> : 'Submit Report'}
                </Button>
            </form>
        </Paper>
    );
}

export default PostLostPage;
